import { v4 as uuidv4 } from 'uuid';
import i18n from 'i18next';
export interface SecureError {
  message: string;
  errorId: string;
  timestamp: string;
  statusCode: number;
}

export interface ErrorLogData {
  errorId: string;
  timestamp: string;
  url?: string;
  method?: string;
  userAgent?: string;
  errorMessage: string;
  stackTrace?: string;
  requestBody?: any;
  requestHeaders?: any;
}

/**
 * Create a secure error response that doesn't expose sensitive information
 */
export function createSecureError(
  originalError: any,
  statusCode: number = 500,
  context?: {
    url?: string;
    method?: string;
    userAgent?: string;
    requestBody?: any;
    requestHeaders?: any;
  }
): SecureError {
  const errorId = uuidv4();
  const timestamp = new Date().toISOString();

  // Log the full error details for debugging
  logError({
    errorId,
    timestamp,
    url: context?.url,
    method: context?.method,
    userAgent: context?.userAgent,
    errorMessage: originalError?.message || 'Unknown error',
    stackTrace: originalError?.stack,
    requestBody: context?.requestBody,
    requestHeaders: context?.requestHeaders,
  });

  return {
    message: getGenericErrorMessage(statusCode),
    errorId,
    timestamp,
    statusCode,
  };
}

/**
 * Log error details securely (server-side only)
 */
function logError(logData: ErrorLogData): void {
  console.error(`[ERROR ${logData.errorId}] ${logData.timestamp}`);

  if (logData.url) {
    console.error(`Request URL: ${logData.url}`);
  }
  if (logData.method) {
    console.error(`Request Method: ${logData.method}`);
  }
  if (logData.userAgent) {
    console.error(`User Agent: ${logData.userAgent}`);
  }

  console.error(`Error Message: ${logData.errorMessage}`);

  if (logData.stackTrace) {
    console.error(`Stack Trace:`, logData.stackTrace);
  }

  if (process.env.NODE_ENV === 'development') {
    if (logData.requestBody) {
      console.log(`[DEV] Request Body:`, logData.requestBody);
    }
    if (logData.requestHeaders) {
      console.log(`[DEV] Request Headers:`, logData.requestHeaders);
    }
  }
}

/**
 * Get generic error messages that don't reveal system details
 */
function getGenericErrorMessage(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return i18n.t('secure-error.invalid-request');
    case 401:
      return i18n.t('secure-error.authentication-required');
    case 403:
      return i18n.t('secure-error.access-denied');
    case 404:
      return i18n.t('secure-error.resource-not-found');
    case 409:
      return i18n.t('secure-error.request-conflict');
    case 422:
      return i18n.t('secure-error.invalid-data');
    case 429:
      return i18n.t('secure-error.too-many-requests');
    case 500:
    default:
      return i18n.t('secure-error.unexpected-error-with-support');
  }
}

/**
 * Sanitize error object to remove sensitive information
 */
export function sanitizeError(error: any): any {
  if (!error) return error;

  // Create a sanitized copy
  const sanitized: any = {
    message: error.message || i18n.t('secure-error.an-error-occurred'),
    status: error.status || 500,
    errorId: error.errorId,
    timestamp: error.timestamp,
  };

  // Only include safe properties
  if (error.statusCode) sanitized.statusCode = error.statusCode;
  if (error.messageCode) sanitized.messageCode = error.messageCode;

  return sanitized;
}

/**
 * Check if an error should trigger a logout (401/403 errors)
 */
export function shouldLogout(error: any): boolean {
  return error?.status === 401 || error?.status === 403;
}

/**
 * Create a user-friendly error message for display
 */
export function createUserFriendlyError(error: any): string {
  if (!error) return i18n.t('secure-error.unexpected-error');

  // If it's already a secure error, use its message
  if (error.message) {
    return error.message;
  }

  // For known error types, provide specific messages
  if (error.status === 401) {
    return i18n.t('secure-error.session-expired');
  }

  if (error.status === 403) {
    return i18n.t('secure-error.no-permission');
  }

  if (error.status === 404) {
    return i18n.t('secure-error.resource-not-found');
  }

  if (error.status === 429) {
    return i18n.t('secure-error.too-many-requests-wait');
  }

  // Default generic message
  return i18n.t('secure-error.unexpected-error-with-support');
}
